import { useTranslation } from "react-i18next";
import { KeyRound, Loader2, Power, ShieldCheck } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

interface UserActionsSectionProps {
  readonly isActive: boolean;
  /** Self guard: the deactivate button is hidden for the viewer's own account. */
  readonly hideDestructiveSelfActions: boolean;
  readonly passwordResetPending: boolean;
  readonly onDeactivate: () => void;
  readonly onReactivate: () => void;
  readonly onChangeRole: () => void;
  readonly onSendPasswordReset: () => void;
}

/** The admin-only account actions card: (de)activate, legacy role change, password reset. */
export function UserActionsSection({
  isActive,
  hideDestructiveSelfActions,
  passwordResetPending,
  onDeactivate,
  onReactivate,
  onChangeRole,
  onSendPasswordReset,
}: UserActionsSectionProps) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="user-detail-actions"
      className="rounded-lg border border-border bg-card p-6"
    >
      <h2 className="brand-eyebrow mb-4">{t("users.detail.actions")}</h2>
      <div className="flex flex-wrap gap-3">
        {isActive && !hideDestructiveSelfActions && (
          <Button data-testid="open-deactivate-user" variant="destructive" onClick={onDeactivate}>
            <Power className="mr-2 h-4 w-4" />
            {t("users.actions.deactivate")}
          </Button>
        )}
        {!isActive && (
          <Button data-testid="open-reactivate-user" onClick={onReactivate}>
            <Power className="mr-2 h-4 w-4" />
            {t("users.actions.reactivate")}
          </Button>
        )}
        <Button data-testid="open-change-role" variant="outline" onClick={onChangeRole}>
          <ShieldCheck className="mr-2 h-4 w-4" />
          {t("users.actions.changeRole")}
        </Button>
        <Button
          data-testid="send-password-reset-button"
          variant="outline"
          onClick={onSendPasswordReset}
          disabled={passwordResetPending}
        >
          {passwordResetPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <KeyRound className="mr-2 h-4 w-4" />
          )}
          {t("users.actions.sendPasswordReset")}
        </Button>
      </div>
    </section>
  );
}
